import { List } from '@/uikit/UserManagement'
import { Checkbox, Divider, State } from '@seiue/ui'
import { RecreateHOC, sortedUniq } from '@seiue/util'
import { Col, Row } from 'antd'
import {
  DataTableProps,
  handleInputValue,
  hasComma,
  TableQuery,
} from 'packages/components/DataTable'
import { $t } from 'packages/locale'
import {
  FieldDomainEnum,
  ReflectionRoleEnum,
  SchoolPluginScope,
  reflectionApi,
} from 'packages/sdks-next/growp'
import React, { useState } from 'react'
import styled, { css } from 'styled-components'

/**
 * 应用编辑 - 选人弹窗-弹窗内容
 *
 * @param param - 参数
 * @param param.scopes - 插件用户范围
 * @param param.setScopes - 设置插件用户范围
 * @param param.setIsChange - 设置是否已变更
 * @param param.setSelectedIds - 设置选择的用户ID
 * @param param.style - 样式
 * @param param.scroll - 滚动设置
 * @returns 组件
 */
export const SelectReflectionContent: React.FC<
  {
    scopes: SchoolPluginScope
    setScopes: (data: SchoolPluginScope) => void
    setIsChange: (data: boolean) => void
    setSelectedIds: (data: number[]) => void
    style?: React.CSSProperties
  } & Pick<DataTableProps, 'scroll'>
> = ({ scopes, setScopes, setIsChange, setSelectedIds, style, scroll }) => {
  const [selectTeacherIds, setSelectTeacherIds] = useState<number[]>(
    scopes.teacherIds
  )

  const [updateSign, setUpdateSign] = useState<number>(0)

  const [selectStudentIds, setSelectStudentIds] = useState<number[]>(
    scopes.studentIds
  )

  const { data: count, loading } = reflectionApi.useCountReflections({
    archivedAtNotEmpty: false,
  })

  const [role, setRole] = useState<
    ReflectionRoleEnum.Teacher | ReflectionRoleEnum.Student
  >(ReflectionRoleEnum.Student)

  const isTeacher = role === ReflectionRoleEnum.Teacher

  const onSelectedRowKeysChange = ({
    studentIds = selectStudentIds,
    teacherIds = selectTeacherIds,
  }: {
    studentIds?: number[]
    teacherIds?: number[]
  }) => {
    const allIds = [...studentIds, ...teacherIds]
    const newIds = sortedUniq(allIds)
    if (JSON.stringify(newIds) !== JSON.stringify(sortedUniq(scopes.ids))) {
      setIsChange(true)
    }
    setScopes({
      ids: newIds,
      studentIds,
      teacherIds,
    })
    setSelectedIds(newIds)
  }

  const fetchAllTeacherIds = async () => {
    const { data } = await reflectionApi.queryTeachers({
      archivedAtNotEmpty: false,
      fields: ['id'],
      paginated: 0,
    })
    return data.map((d) => d.id)
  }

  const fetchAllStudentIds = async () => {
    const { data } = await reflectionApi.queryStudents({
      archivedAtNotEmpty: false,
      fields: ['id'],
      paginated: 0,
    })
    return data.map((d) => d.id)
  }

  if (loading || count === null) return <State.Loading />

  const selectedAll =
    selectTeacherIds.length + selectStudentIds.length === count.count

  return (
    <Box style={style}>
      <Row style={{ flexWrap: 'nowrap' }}>
        <CheckCol span={8}>
          <Item $readonly={true}>
            <Flex>
              <Checkbox
                indeterminate={
                  selectTeacherIds.length + selectStudentIds.length <
                    count.count &&
                  (!!selectStudentIds.length || !!selectTeacherIds.length)
                }
                checked={selectedAll}
                onClick={async () => {
                  if (selectedAll) {
                    onSelectedRowKeysChange({
                      teacherIds: [],
                      studentIds: [],
                    })
                    setSelectTeacherIds([])
                    setSelectStudentIds([])
                  } else {
                    const allTeacherIds = await fetchAllTeacherIds()
                    const allStudentIds = await fetchAllStudentIds()
                    onSelectedRowKeysChange({
                      teacherIds: allTeacherIds,
                      studentIds: allStudentIds,
                    })
                    setSelectTeacherIds(allTeacherIds)
                    setSelectStudentIds(allStudentIds)
                  }
                  setUpdateSign((prev) => prev + 1)
                }}
              />
              <Label> {$t('全选')}</Label>
            </Flex>
            <CheckSum>{`已选择 ${
              selectTeacherIds.length + selectStudentIds.length
            }/${count.count} 人`}</CheckSum>
          </Item>
          <Divider />
          <Item
            $selected={role === ReflectionRoleEnum.Student}
            onClick={() => {
              setRole(ReflectionRoleEnum.Student)
              setUpdateSign((prev) => prev + 1)
            }}
          >
            <Flex>
              <Checkbox
                indeterminate={
                  selectStudentIds.length > 0 &&
                  selectStudentIds.length < count.studentCount
                }
                checked={
                  count.studentCount > 0 &&
                  count.studentCount === selectStudentIds.length
                }
                onClick={async () => {
                  if (
                    count.studentCount > 0 &&
                    count.studentCount === selectStudentIds.length
                  ) {
                    onSelectedRowKeysChange({
                      studentIds: [],
                    })
                    setSelectStudentIds([])
                  } else {
                    const allStudentIds = await fetchAllStudentIds()
                    onSelectedRowKeysChange({
                      studentIds: allStudentIds,
                    })
                    setSelectStudentIds(allStudentIds)
                  }
                  setUpdateSign((prev) => prev + 1)
                }}
              />
              <Label> {$t('在校学生')}</Label>
            </Flex>
            <CheckNum>{`${selectStudentIds.length}/${count.studentCount}`}</CheckNum>
          </Item>
          <Divider />
          <Item
            $selected={role === ReflectionRoleEnum.Teacher}
            onClick={() => {
              setRole(ReflectionRoleEnum.Teacher)
              setUpdateSign((prev) => prev + 1)
            }}
          >
            <Flex>
              <Checkbox
                indeterminate={
                  selectTeacherIds.length > 0 &&
                  selectTeacherIds.length < count.teacharCount
                }
                checked={
                  count.teacharCount > 0 &&
                  count.teacharCount === selectTeacherIds.length
                }
                onClick={async () => {
                  if (
                    count.teacharCount > 0 &&
                    count.teacharCount === selectTeacherIds.length
                  ) {
                    onSelectedRowKeysChange({
                      teacherIds: [],
                    })
                    setSelectTeacherIds([])
                  } else {
                    const allTeacherIds = await fetchAllTeacherIds()
                    onSelectedRowKeysChange({
                      teacherIds: allTeacherIds,
                    })
                    setSelectTeacherIds(allTeacherIds)
                  }
                  setUpdateSign((prev) => prev + 1)
                }}
              />
              <Label>{$t('在校教师')}</Label>
            </Flex>
            <CheckNum>{`${selectTeacherIds.length}/${count.teacharCount}`}</CheckNum>
          </Item>
          <Divider />
        </CheckCol>
        <TableCol style={{ width: 'calc(100% - 264px)' }}>
          <RecreateHOC dep={updateSign}>
            <List
              key={'t'}
              domain={
                isTeacher
                  ? FieldDomainEnum.FormalTeacher
                  : FieldDomainEnum.FormalStudent
              }
              formatSearchParams={(props) => {
                // 对 nameLike 和 usinLike 进行特殊处理，当这两个参数的值里包含中英文逗号、顿号、分号、换行符时，将 nameLike 和 usinLike 的值替换为 nameIn 和 usingIn 的值，并且将值变成用英文逗号分隔的值
                if (props['nameLike'] && hasComma(props['nameLike'])) {
                  props['nameIn'] = handleInputValue(props['nameLike'])
                  delete props['nameLike']
                }

                if (props['usinLike'] && hasComma(props['usinLike'])) {
                  props['usinIn'] = handleInputValue(props['usinLike'])
                  delete props['usinLike']
                }

                return props
              }}
              api={(q: TableQuery) => {
                if (isTeacher) {
                  return reflectionApi.queryTeachers({
                    ...q,
                    archivedAtNotEmpty: false,
                    expand: ['fields'],
                  })
                }

                return reflectionApi.queryStudents({
                  ...q,
                  archivedAtNotEmpty: false,
                  expand: ['fields', 'guardians'],
                })
              }}
              apiName={
                isTeacher
                  ? 'Reflection.queryTeachers'
                  : 'Reflection.queryStudents'
              }
              rowAction={[]}
              fieldSettingLocation={
                isTeacher
                  ? 'InternalTeachersFieldSetting'
                  : 'InternalStudentsFieldSetting'
              }
              hiddenFieldSettings={true}
              setOutputSelectedRowKeys={(rowIds) => {
                if (isTeacher) {
                  setSelectTeacherIds(rowIds)
                  onSelectedRowKeysChange({
                    teacherIds: rowIds,
                  })
                } else {
                  setSelectStudentIds(rowIds)
                  onSelectedRowKeysChange({
                    studentIds: rowIds,
                  })
                }
              }}
              outputSelectedRowKeys={
                isTeacher ? scopes.teacherIds : scopes.studentIds
              }
              showSelectedCheck={true}
              scroll={scroll}
            />
          </RecreateHOC>
        </TableCol>
      </Row>
    </Box>
  )
}

const Box = styled.div`
  width: 960px;
  height: calc(100vh - 105px);
  background-color: #fff;
  border-radius: 12px;
`

const CheckCol = styled(Col)`
  padding: 20px;
  max-width: 264px;
`

const Item = styled.div<{ $selected?: boolean; $readonly?: boolean }>`
  display: flex;
  padding: 16px;
  justify-content: space-between;

  background-color: ${(props) =>
    props.$selected ? props.theme.brand._5 : '#fff'};
  ${({ $readonly }) =>
    !$readonly &&
    css`
      &:hover {
        background-color: ${(props) => props.theme.brand._5};
      }
      cursor: pointer;
    `}

  border-radius: 8px;
`

const Flex = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const Label = styled.div`
  color: ${(props) => props.theme.text._1};
  font-size: 14px;
  height: 22px;
  line-height: 22px;
`

const CheckLabel = styled.div`
  color: ${(props) => props.theme.text._1};
  font-size: 14px;
  height: 22px;
  line-height: 22px;
`

const CheckNum = styled.div`
  color: ${(props) => props.theme.text._4};
  font-size: 12px;
  line-height: 20px;
  height: 20px;
`

const CheckSum = styled.div`
  color: ${(props) => props.theme.text._1};
  font-size: 12px;
  line-height: 20px;
  height: 20px;
`

const TableCol = styled(Col)`
  border-left: 1px solid ${(props) => props.theme.border.greyNormal};
  padding: 20px 20px 0px 20px;
`
